import { defineStore } from 'pinia';
import {computed, watch} from 'vue';
import { materials } from "./js/materialService";

export const useImageEditorStore = defineStore('image-editor', {
    state: () => ({
        loadingState: {},
        processing: false,
        canvas: null,

        mask_type: 'square',
        mask: null,
        maskData: {
            square: {
                type: 'square',
                matches: 'square', // key to compare to in price table
            },
            circle: {
                type: 'circle',
                matches: 'circle',
            },
            rounded_corners: {
                type: 'rounded_corners',
                matches: 'rounded_corners',
                radius: 20,
            },
            die_cut_small: {
                type: 'die_cut_small',
                matches: 'die_cut',
                stroke_width: 8,
            },
            die_cut_medium: {
                type: 'die_cut_medium',
                matches: 'die_cut',
                stroke_width: 20,
            },
            die_cut_custom: {
                type: 'die_cut_custom',
                matches: 'die_cut',
                stroke_width: 45,
            }
        },

        can_edit_background: true,
        background: null,
        bg_color: '#fff',
        bg_image: null,

        text: {
            fontFamily: 'Roboto',
            textAlign: 'left',
            textAlignSelect: ['left', 'center', 'right'],
            fontColor: 'black',
            diameter: 10000,
            textMessage: 'Message',
            fontSize: 65,
            flipped: false,
            kerning: 0,

            old_fontSize: null,
        },

        material_id: 1,

        width: "2",
        height: "2",
        quantity: "100",
        message: "",
        total_amount: "333",
        laminate: 'glossy',

        instruction: "",
        instruction_files: [],

        priceData: {},
        addingToCart: false,
        cart_id: null,

        editorNav: null,
        mediaOptions: {
            mimes: '.png,.jpg,.jpeg,.svg,.pdf,.tif,.gif,.bmp,.avif',
            mimeTypes: null,
            maxFileCount: null,
            maxFileSize: 4,
        },

        warnings: {},

        canUndo: true,
        canRedo: false,
    }),
    getters: {
        material() {
            return materials.find(material => material.id === this.material_id) || null;
        },
        fontSelect() {
            const url = "@import url('https://fonts.googleapis.com/css2?family=Barriecito&family=Comic+Neue&family=Days+One&family=Fontdiner+Swanky&family=Gluten&family=Indie+Flower&family=Inter:wght@100;200;300;400;500;600;700;800&family=Permanent+Marker&family=Press+Start+2P&family=Quicksand&family=Qwitcher+Grypen:wght@700&family=Roboto&family=Rubik+Moonrocks&family=Sedgwick+Ave+Display&family=Special+Elite&family=League+Gothic&family=League+Spartan:wght@500&family=Mukta:wght@500&family=Open+Sans:wght@500&display=swap');";
            const matches = url.match(/family=([^&:]*)/g);
            return matches.map(match => decodeURIComponent(match.replace('family=', '').replace(/\+/g, ' ')));
        },
        getMaterials() {
            return Object.keys(this.priceData ?? {});
        },
        getMaskTypes() {
            let app = this;
            const materialData = this.priceData?.[this.material.title] ?? {};
            const mask_types = Object.keys(materialData);

            if (!mask_types.includes('any')) {
                // Price table has an "any" mask type
                if(!mask_types.includes(this.maskData[this.getMaskTypeSelector()].matches)) {
                    Object.keys(app.maskData).forEach(type => {
                        if (mask_types.includes(app.maskData[type].matches)) {
                            app.mask_type = type;
                        }
                    });
                }
            }

            return mask_types;
        },
        getWidths() {
            const materialData = this.priceData?.[this.material.title] ?? {};
            const maskTypeData = {...(materialData[this.getMaskTypeSelector()] || {}), ...(materialData['any'] || {})};
            const widths = Object.keys(maskTypeData);
            if (!widths.includes(this.width)) {
                this.width = widths[widths.length - 1];
            }
            return widths;
        },
        getHeights() {
            const materialData = this.priceData?.[this.material.title] ?? {};
            const maskTypeData = {...(materialData[this.getMaskTypeSelector()] || {}), ...(materialData['any'] || {})};
            const widthData = maskTypeData[this.width] || {};
            const heights = Object.keys(widthData);
            if (!heights.includes(this.height)) {
                this.height = heights[heights.length - 1];
            }
            return heights;
        },
        getAmounts() {
            const materialData = this.priceData?.[this.material.title] ?? {};
            const maskTypeData = {...(materialData[this.getMaskTypeSelector()] || {}), ...(materialData['any'] || {})};
            const widthData = maskTypeData[this.width] || {};
            const heightData = widthData[this.height] || {};
            const amounts =  Object.keys(heightData);
            if (!amounts.includes(this.quantity)) {
                this.quantity = amounts[0] || this.quantity;
            }
            return amounts;
        },
        getPrice() {
            const materialData = this.priceData?.[this.material.title] ?? {};
            const maskTypeData = {...(materialData[this.getMaskTypeSelector()] || {}), ...(materialData['any'] || {})};
            const widthData = maskTypeData[this.width] || {};
            const heightData = widthData[this.height] || {};
            return heightData[parseInt(this.quantity)] || '';
        },
        loading() {
            function isEmpty(obj) {
                return Object.keys(obj).length === 0;
            }

            if (isEmpty(this.loadingState)) {
                return null;
            }

            let firstKey = Object.keys(this.loadingState)[0];
            return this.loadingState[firstKey];
        }
    },
    actions: {
        onTextOptionsChanged(callback) {
            const textState = computed(() => ({ ...this.text }));

            // Watch the computed property
            watch(textState, callback, { deep: true });
        },
        onDimensionsChanged(callback) {
            watch(() => this.width, callback);
            watch(() => this.height, callback);
        },
        setPriceData(priceData) {
            this.$patch({ priceData: priceData });
        },
        getMaskTypeSelector() {
            let mask_type = this.mask_type;
            if (['die_cut_small', 'die_cut_medium', 'die_cut_custom'].includes(mask_type)) {
                return 'die_cut';
            }
            return mask_type;
        },
        setLoading(type, title) {
            this.loadingState = { ...this.loadingState, [type]: { title: title } };

            const hasProcessingKey = Object.keys(this.loadingState).some(key => key.startsWith("processing"));
            if (hasProcessingKey) {
                this.processing = true;
            }
        },
        setLoadingFinished(type) {
            const { [type]: _, ...rest } = this.loadingState;
            this.loadingState = rest;

            if (this.processing) {
                const hasProcessingKey = Object.keys(this.loadingState).some(key => key.startsWith("processing"));
                if (!hasProcessingKey) {
                    this.processing = false;
                    window.imageEditor.onProcessingFinished();
                }
            }
        },
        setMaskData(type, newData) {
            if (this.maskData[type]) {
                this.maskData[type] = { ...this.maskData[type], ...newData };
            }
        },
        addWarning(key, message) {
            const newWarnings = { ...this.warnings };
            newWarnings[key] = message;
            this.warnings = newWarnings;
        },
        removeWarning(key) {
            const newWarnings = { ...this.warnings };
            delete newWarnings[key];
            this.warnings = newWarnings;
        }

    },
})
